// src/pages/brands/forms/BrandServicesForm.jsx
import React from 'react';
import { Settings as SettingsIcon } from '@mui/icons-material';

// Import the shared EntityServicesForm component
import { EntityServicesForm } from '../../../components/shared/forms';
import { PLATFORM_OPTIONS } from '../../../components/features/content/form-utils/contentFormData';

// Platform options 
const platforms = PLATFORM_OPTIONS;

/**
 * Brand services form component
 * Uses the shared EntityServicesForm with brand-specific configuration
 */
const BrandServicesForm = ({ 
  brand, 
  onChange, 
  onNestedChange,
  onDeepNestedChange,
  setFieldValue, // Added for Formik support
  errors = {},
  touched = {},
  data, // Support for the newer data prop pattern
  requiredFields = []
}) => {
  // Support both brand and data prop patterns for backward compatibility
  const formData = data || brand || {};
  
  // Default service configuration if none exists
  const defaultConfig = {
    platforms: {
      facebook: false,
      instagram: false,
      linkedin: false,
      twitter: false,
      tiktok: false,
      youtube: false,
      snapchat: false
    },
    services: {
      socialMediaManagement: false,
      contentCreation: false,
      campaignManagement: false,
      influencerMarketing: false,
      analyticsReporting: false,
      communityManagement: false,
      paidAdvertising: false,
      brandingService: false,
      packagingService: false
    },
    postingFrequency: 'alternate_days',
    customFrequency: 2,
    reportingFrequency: 'monthly',
    minPosts: 10,
    maxPosts: 30
  };
  
  // Format errors for nested fields
  const formattedErrors = {
    ...errors,
    serviceConfig: {
      ...(typeof errors.serviceConfig === 'object' ? errors.serviceConfig : {}),
      platforms: errors['serviceConfig.platforms'],
      services: errors['serviceConfig.services'],
      postingFrequency: errors['serviceConfig.postingFrequency'],
      reportingFrequency: errors['serviceConfig.reportingFrequency'],
      minPosts: errors['serviceConfig.minPosts'],
      maxPosts: errors['serviceConfig.maxPosts']
    }
  };
  
  // Format touched state for nested fields
  const formattedTouched = {
    ...touched,
    serviceConfig: {
      ...(typeof touched.serviceConfig === 'object' ? touched.serviceConfig : {}),
      platforms: touched['serviceConfig.platforms'],
      services: touched['serviceConfig.services'],
      postingFrequency: touched['serviceConfig.postingFrequency'],
      reportingFrequency: touched['serviceConfig.reportingFrequency'],
      minPosts: touched['serviceConfig.minPosts'],
      maxPosts: touched['serviceConfig.maxPosts']
    }
  };
  
  // Define available services
  const availableServices = [
    { 
      id: 'socialMediaManagement', 
      label: 'Social Media Management', 
      description: 'Full management of social media accounts' 
    },
    { 
      id: 'contentCreation', 
      label: 'Content Creation', 
      description: 'Creation of posts, graphics, and videos' 
    },
    { 
      id: 'campaignManagement', 
      label: 'Campaign Management', 
      description: 'Creating and running marketing campaigns' 
    },
    { 
      id: 'influencerMarketing', 
      label: 'Influencer Marketing', 
      description: 'Working with influencers for brand promotion' 
    },
    { 
      id: 'analyticsReporting', 
      label: 'Analytics & Reporting', 
      description: 'Performance tracking and reporting' 
    },
    { 
      id: 'communityManagement', 
      label: 'Community Management', 
      description: 'Engaging with followers and managing comments' 
    },
    { 
      id: 'paidAdvertising', 
      label: 'Paid Advertising', 
      description: 'Managing paid social media campaigns' 
    },
    { 
      id: 'brandingService', 
      label: 'Branding Service', 
      description: 'Brand identity design and development' 
    },
    { 
      id: 'packagingService', 
      label: 'Packaging Service', 
      description: 'Product packaging design and consulting' 
    }
  ];
  
  // Handle field changes with Formik support
  const handleChange = (name, value) => {
    if (setFieldValue) {
      setFieldValue(name, value);
    } else if (onChange) {
      onChange(name, value);
    }
  };
  
  // Handle nested field changes with Formik support
  const handleNestedChange = (parent, field, value) => {
    if (setFieldValue) {
      setFieldValue(`${parent}.${field}`, value);
    } else if (onNestedChange) {
      onNestedChange(parent, field, value);
    }
  };
  
  // Handle deeply nested field changes
  const handleDeepNestedChange = (parent, child, field, value) => {
    if (setFieldValue) {
      setFieldValue(`${parent}.${child}.${field}`, value);
    } else if (onDeepNestedChange) {
      onDeepNestedChange(parent, child, field, value);
    }
  };
  
  // Determine required fields
  const isRequired = (field) => {
    return requiredFields.includes(field) || 
           requiredFields.includes(`serviceConfig.${field}`);
  };
  
  return (
    <EntityServicesForm
      data={formData}
      defaultConfig={defaultConfig}
      onChange={handleChange}
      onNestedChange={handleNestedChange}
      onDeepNestedChange={handleDeepNestedChange}
      errors={formattedErrors}
      touched={formattedTouched}
      configField="serviceConfig"
      availableServices={availableServices}
      title="Service Configuration"
      description="Configure the services and platforms for this brand"
      icon={<SettingsIcon />}
      showPlatforms={true}
      showServices={true}
      showPostingFrequency={true}
      showReportingFrequency={true}
      showPostLimits={true}
      requiredPlatforms={isRequired('platforms')}
      requiredServices={isRequired('services')}
      requiredPostingFrequency={isRequired('postingFrequency')}
      requiredReportingFrequency={isRequired('reportingFrequency')}
      requiredPostLimits={isRequired('minPosts') || isRequired('maxPosts')}
    />
  );
};

export default BrandServicesForm;